import { modeDuration } from "@/lib/interviewModes";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useInvite } from "@/hooks/useInvite";
import { candidateApi } from "@/lib/candidateApi";
import { returningRoute } from "@/lib/inviteRouting";
import { CandidateFlow } from "@/shell/CandidateFlow";
import { Alert, Button, Card, Chip, Spinner } from "@/components/primitives";
import { InviteError } from "./InviteError";

// Invite landing (Jinja: invite_landing.html). Copy ported 1:1.
function DetailRow({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex items-center gap-3 py-1.5">
      <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-accent-50 text-accent">
        <svg width="15" height="15" viewBox="0 0 20 20" fill="none" aria-hidden="true">
          <path d="M5 10.5l3.5 3.5L15 6.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </span>
      <span className="text-sm text-slate-700">{children}</span>
    </div>
  );
}

type InviteDataWithToken = {
  token: string;
};

// Local fallback only: holds the accepted version when the server could not
// record it yet (network failure, or a returning candidate who still has to
// verify; OtpVerify sends it once they have).
export function consentStorageKey(token?: string, invite?: InviteDataWithToken | null) {
  const fallback = token || invite?.token;
  return fallback ? `ic-candidate-privacy-consent:${fallback}` : null;
}

export function InviteLanding() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const { data, error, loading } = useInvite(token);

  const [privacyAccepted, setPrivacyAccepted] = useState(false);
  const [privacyCheck, setPrivacyCheck] = useState(false);
  const [consentError, setConsentError] = useState<string | null>(null);

  // Returning candidates skip the pitch and go where returningRoute sends them:
  // home if a cookie already proves this device, otherwise a re-auth screen.
  // The link never signs anyone in by itself, so this is a plain redirect.
  // Returning candidates also see the notice when it has changed since they
  // agreed, so the redirect waits until it is accepted.
  const dest = data && privacyAccepted ? returningRoute(data) : null;

  useEffect(() => {
    if (dest && !consentError) navigate(dest, { replace: true });
  }, [dest, consentError, navigate]);

  useEffect(() => {
    if (!data) {
      setPrivacyAccepted(false);
      return;
    }
    if (data.privacyAcceptedVersion === data.privacyNoticeVersion) {
      setPrivacyAccepted(true);
      return;
    }
    const key = consentStorageKey(token, data);
    try {
      setPrivacyAccepted(!!key && window.localStorage.getItem(key) === data.privacyNoticeVersion);
    } catch {
      setPrivacyAccepted(false);
    }
  }, [token, data]);

  async function acceptPrivacyNotice() {
    if (!data) return;
    const version = data.privacyNoticeVersion;
    setPrivacyCheck(false);
    setConsentError(null);
    // Recording needs a verified invite. A first-time visitor's link already
    // verifies it (auth/token); a returning one must enter the emailed code first.
    const verified = data.verified || (await candidateApi.authByToken(data.token)).ok;
    const saved = verified && (await candidateApi.recordPrivacyConsent(data.token, version)).ok;
    if (!saved) {
      const key = consentStorageKey(token, data);
      try {
        if (key) window.localStorage.setItem(key, version);
      } catch {
        // Local-storage is optional in some privacy-first browsers; consent stays in memory.
      }
      if (verified) setConsentError("We could not save your agreement. It is kept on this device for now; please try again later.");
    }
    setPrivacyAccepted(true);
  }

  if (loading || (dest && !consentError)) {
    return (
      <CandidateFlow>
        <div className="flex justify-center py-10">
          <Spinner size={28} />
        </div>
      </CandidateFlow>
    );
  }
  if (error || !data) return <InviteError message={error ?? "This invite link is not valid."} />;

  const duration = modeDuration(data.mode);
  const agency = data.agencyName || "Your consultant";
  const lightLogo = data.branding?.logoUrlLight || undefined;
  const logoFilter = data.branding?.logoFilter;

  function begin() {
    if (!data) return;
    // If already verified, skip demo and go home. Otherwise start with demo (C2).
    if (data.verified) {
      navigate(`/invite/${data.token}/home`);
    } else {
      navigate(`/invite/${data.token}/demo`);
    }
  }

  // Personalised headline: lead with the candidate's specific role + company (C1 spec)
  const hasRoleContext = data.role && data.company;
  const headline = hasRoleContext
    ? `You are preparing for ${data.role} at ${data.company}.`
    : `${agency} has invited you to prepare for your interview${data.company ? ` at ${data.company}` : ""}.`;

  return (
    <CandidateFlow agencyName={data.agencyName} logoUrl={lightLogo} logoFilter={logoFilter}>
      <Card className="space-y-5">
        <div>
          <h1 className="text-xl font-semibold leading-snug text-ink">
            {headline}
          </h1>
          {hasRoleContext && (
            <p className="mt-2 text-sm leading-relaxed text-slate-500">
              This session will make you more prepared for this interview than most candidates in the market.
            </p>
          )}
        </div>

        <div className="flex flex-wrap gap-2">
          <Chip variant="accent">{data.personaLabel}</Chip>
          <Chip variant="dim">{data.modeLabel}</Chip>
        </div>

        <p className="text-sm leading-relaxed text-slate-500">
          AI-powered candidate preparation interview{duration ? `, approximately ${duration}` : ""}. You'll receive a
          personalised scorecard at the end.
        </p>

        <div className="space-y-1">
          <DetailRow>Voice-led, no typing required</DetailRow>
          {duration && <DetailRow>Approximately {duration}</DetailRow>}
          <DetailRow>Scorecard shared with your consultant, and yours to view too</DetailRow>
        </div>

        <p className="rounded-md border border-hair bg-paper-2 px-4 py-3.5 text-xs leading-relaxed text-slate-500">
          Privacy: Your responses and scorecard are available to you and your consultant through
          the portal. Our service providers process data to deliver this service. Read our{" "}
          <a href="/privacy" className="text-accent underline">privacy policy</a> for details.
        </p>

        {consentError && <Alert variant="error">{consentError}</Alert>}

        {!privacyAccepted && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-ink/20 px-6 backdrop-blur-sm">
            <div
              role="dialog"
              aria-modal="true"
              aria-labelledby="privacy-notice-title"
              aria-describedby="privacy-notice-body"
              className="w-full max-w-sm rounded-xl border border-hair bg-cream p-6 shadow-lg"
              data-testid="privacy-notice-dialog"
            >
              <h2 id="privacy-notice-title" className="text-lg font-semibold text-ink">
                Candidate privacy notice
              </h2>
              <p id="privacy-notice-body" className="mt-2 text-sm leading-relaxed text-slate-500">
                Your answers and scorecard are shared with you and your consultant through the
                portal. Our service providers process this data only to run the session. We use
                it to prepare tailored interview feedback and a scorecard for you both.
              </p>
              <label className="mt-4 flex items-start gap-2 text-sm text-slate-500">
                <input
                  type="checkbox"
                  checked={privacyCheck}
                  onChange={(e) => setPrivacyCheck(e.target.checked)}
                  className="mt-1"
                  data-testid="privacy-notice-checkbox"
                />
                <span>
                  I have read the privacy policy and agree to the processing described there.
                </span>
              </label>
              <Button
                variant="accent"
                className="mt-5 w-full"
                onClick={acceptPrivacyNotice}
                disabled={!privacyCheck}
                data-testid="privacy-notice-accept"
              >
                I agree and continue
              </Button>
              <p className="mt-3 text-xs text-slate-400">Read the full policy first: <a href="/privacy" className="text-accent underline">privacy policy</a>.</p>
            </div>
          </div>
        )}

        {data.linkedinEnabled && (
          <>
            <a
              href={`/auth/linkedin/start/${data.token}`}
              className="flex w-full items-center justify-center gap-2 rounded-xl border border-hair bg-paper py-3 text-sm font-semibold text-ink hover:bg-cream"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="#0A66C2" aria-hidden="true">
                <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
              </svg>
              Continue with LinkedIn
            </a>
            <div className="my-3 text-center text-xs text-slate-400">or</div>
          </>
        )}

        <Button
          variant="accent"
          className="w-full"
          onClick={begin}
          disabled={!privacyAccepted}
          data-testid="invite-landing-start"
        >
          Get started
          <svg width="16" height="16" viewBox="0 0 20 20" fill="none" aria-hidden="true">
            <path d="M4 10h12M11 5l5 5-5 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </Button>
      </Card>
    </CandidateFlow>
  );
}
